
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CreditCard, CheckCircle, AlertTriangle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import RegistrationGuard from "@/components/auth/RegistrationGuard";
import { checkTimeSlotConflict } from "@/utils/availabilityUtils";

// Mock psychologist data - in real app would come from API
const psychologistData = {
  'psy1': {
    id: 'psy1',
    name: 'Dra. Ana Silva',
    price: 150,
    photoUrl: "https://randomuser.me/api/portraits/women/44.jpg"
  },
  'psy2': {
    id: 'psy2',
    name: 'Dr. Carlos Mendes', 
    price: 160,
    photoUrl: "https://randomuser.me/api/portraits/men/32.jpg"
  },
  'psy3': {
    id: 'psy3',
    name: 'Dra. Juliana Costa',
    price: 170,
    photoUrl: "https://randomuser.me/api/portraits/women/68.jpg"
  }
};

const ScheduleStep3 = () => {
  const navigate = useNavigate();
  const { profile, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState("");
  const [appointmentData, setAppointmentData] = useState<any>(null);

  useEffect(() => {
    // Get stored data from previous steps
    const selectedPsychologist = sessionStorage.getItem('selectedPsychologist');
    const selectedDate = sessionStorage.getItem('selectedDate');
    const selectedTime = sessionStorage.getItem('selectedTime');
    const duration = sessionStorage.getItem('duration') || '60';
    const selectedPrice = sessionStorage.getItem('price');

    console.log('🔍 [Step3 DEBUG] Dados recuperados do sessionStorage:', {
      selectedPsychologist,
      selectedDate,
      selectedTime,
      duration,
      selectedPrice
    });

    if (!selectedPsychologist || !selectedDate || !selectedTime) {
      navigate('/schedule/step1');
      return;
    }

    const finalPrice = selectedPrice ? parseFloat(selectedPrice) : null;

    // Try to find psychologist in mock data first, then fetch from database
    let psychologist = psychologistData[selectedPsychologist as keyof typeof psychologistData];
    
    if (!psychologist) {
      // Fetch psychologist data from database
      const fetchPsychologist = async () => {
        try {
          const { data, error } = await supabase
            .from('psychologists')
            .select('*')
            .eq('id', selectedPsychologist)
            .single();

          if (error) {
            navigate('/schedule/step1');
            return;
          }

          const formattedPsychologist = {
            id: data.id,
            name: data.name,
            price: finalPrice || data.price || 150,
            photoUrl: data.photo_url || "https://randomuser.me/api/portraits/women/1.jpg"
          };

          console.log('💰 [Step3 DEBUG] Preço final definido:', {
            selectedPrice: finalPrice,
            databasePrice: data.price,
            finalPrice: formattedPsychologist.price
          });

          setAppointmentData({
            psychologist: formattedPsychologist,
            date: parseISO(selectedDate),
            time: selectedTime,
            duration: parseInt(duration)
          });
        } catch (error) {
          navigate('/schedule/step1');
        }
      };

      fetchPsychologist();
    } else {
      const updatedPsychologist = {
        ...psychologist,
        price: finalPrice || psychologist.price
      };

      console.log('💰 [Step3 DEBUG] Mock - Preço final definido:', {
        selectedPrice: finalPrice,
        mockPrice: psychologist.price,
        finalPrice: updatedPsychologist.price
      });

      setAppointmentData({
        psychologist: updatedPsychologist,
        date: parseISO(selectedDate),
        time: selectedTime,
        duration: parseInt(duration)
      });
    }
  }, [navigate]);

  // Validate slot availability when appointment data is loaded
  useEffect(() => {
    const validateSlot = async () => {
      if (!appointmentData) return;
      
      setIsValidating(true);
      try {
        const dateString = format(appointmentData.date, 'yyyy-MM-dd');
        const hasConflict = await checkTimeSlotConflict(
          appointmentData.psychologist.id,
          dateString,
          appointmentData.time
        );
        
        if (hasConflict) {
          setError("Este horário não está mais disponível. Por favor, retorne e selecione outro horário.");
          toast.error("Horário não disponível", {
            description: "Este horário foi reservado por outro usuário. Por favor, escolha outro horário."
          });
        }
      } catch (error) {
        setError("Erro ao validar disponibilidade do horário. Por favor, tente novamente.");
      } finally {
        setIsValidating(false);
      }
    };
    
    validateSlot();
  }, [appointmentData]);

  const handleBack = () => {
    navigate('/schedule/step2');
  };

  const handlePayment = async () => {
    if (!appointmentData || !profile || !user) return;

    setIsLoading(true);
    setError("");

    try {
      // Final validation before creating appointment
      const dateString = format(appointmentData.date, 'yyyy-MM-dd');
      const hasConflict = await checkTimeSlotConflict(
        appointmentData.psychologist.id,
        dateString,
        appointmentData.time
      );

      if (hasConflict) {
        throw new Error('Este horário já foi reservado. Por favor, escolha outro horário.');
      }
      
      console.log('💳 Criando appointment com os dados:', {
        psychologist_id: appointmentData.psychologist.id,
        user_id: user.id,
        date: dateString,
        time: appointmentData.time,
        duration: appointmentData.duration,
        total_price: appointmentData.psychologist.price
      });

      // Create appointment first
      const { data: appointment, error: appointmentError } = await supabase
        .from('appointments')
        .insert({
          psychologist_id: appointmentData.psychologist.id,
          user_id: user.id,
          date: dateString,
          time: appointmentData.time,
          duration: appointmentData.duration,
          total_price: appointmentData.psychologist.price,
          status: 'pending',
          payment_status: 'pending'
        })
        .select()
        .single();

      if (appointmentError) {
        console.error('❌ Erro ao criar appointment:', appointmentError);
        if (appointmentError.code === '23505') {
          throw new Error('Você já possui um agendamento para este horário');
        }
        throw new Error('Erro ao criar consulta: ' + appointmentError.message);
      }

      console.log('✅ Appointment criado:', appointment);

      const patientName = profile.full_name || 
        (profile.first_name && profile.last_name ? `${profile.first_name} ${profile.last_name}` : 'Paciente');

      console.log('🔄 Iniciando checkout do Mercado Pago...');
      toast.info('Criando checkout...', { description: 'Aguarde enquanto preparamos o pagamento' });

      const { data, error: checkoutError } = await supabase.functions.invoke('mercado-pago-checkout', {
        body: {
          appointmentId: appointment.id,
          amount: appointmentData.psychologist.price,
          psychologistName: appointmentData.psychologist.name,
          patientName: patientName,
          appointmentDate: dateString,
          appointmentTime: appointmentData.time,
          duration: appointmentData.duration
        }
      });

      console.log('📨 Resposta do checkout:', { data, error: checkoutError });

      if (checkoutError) {
        console.error('❌ Erro no checkout:', checkoutError);
        throw new Error(checkoutError.message || 'Erro ao processar pagamento');
      }

      if (!data?.checkoutUrl) {
        console.error('❌ URL de checkout não retornada:', data);
        throw new Error('URL de checkout não foi gerada');
      }

      // Clear session storage before redirect
      sessionStorage.removeItem('selectedPsychologist');
      sessionStorage.removeItem('selectedDate');
      sessionStorage.removeItem('selectedTime');
      sessionStorage.removeItem('duration');
      sessionStorage.removeItem('price');
      
      console.log('🚀 Redirecionando para:', data.checkoutUrl);
      toast.success('Checkout criado!', { description: 'Redirecionando para o Mercado Pago...' });
      
      // Force immediate redirect using window.location.href
      setTimeout(() => {
        console.log('🔄 Executando redirecionamento...');
        window.location.href = data.checkoutUrl;
      }, 500);
      
    } catch (error: any) {
      console.error('❌ Erro completo no pagamento:', error);
      const errorMessage = error?.message || "Erro ao gerar pagamento. Tente novamente.";
      setError(errorMessage);
      toast.error('Erro no pagamento', { description: errorMessage });
      setIsLoading(false);
    }
  };

  if (!appointmentData) {
    return (
      <RegistrationGuard>
        <div className="min-h-screen bg-gray-50 py-8">
          <div className="max-w-2xl mx-auto px-4">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-gray-200 rounded w-64"></div>
              <div className="h-4 bg-gray-200 rounded w-96"></div>
              <div className="bg-white rounded-lg p-6 space-y-4">
                <div className="h-6 bg-gray-200 rounded w-48"></div>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-5 bg-gray-200 rounded w-32"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="flex justify-between">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </RegistrationGuard>
    );
  }

  const consultationPrice = appointmentData.psychologist.price;
  const totalPrice = consultationPrice;
  const hasValidationError = error && error.includes("não está mais disponível");

  return (
    <RegistrationGuard>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4 space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Finalizar Agendamento</h1>
            <p className="text-gray-600 mt-2">
              Revise os detalhes e efetue o pagamento para confirmar sua consulta
            </p>
          </div>

          {/* Validation status */}
          {isValidating && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Verificando disponibilidade do horário...
              </AlertDescription>
            </Alert>
          )}

          {/* DEBUG: Verificação de Preços */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm text-green-800">
              🔍 <strong>DEBUG - Verificação de Preços:</strong><br/>
              Preço sendo usado: <strong>R$ {consultationPrice.toFixed(2)}</strong><br/>
              Duração: <strong>{appointmentData.duration} minutos</strong>
            </div>
          </div>

          {/* Resumo da Consulta Card */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Resumo da Consulta
              </CardTitle>
              <CardDescription>
                Confirme os detalhes do seu agendamento
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Psychologist info */}
              <div className="flex items-center space-x-4 pb-4">
                <div className="w-16 h-16 rounded-full overflow-hidden">
                  <img 
                    src={appointmentData.psychologist.photoUrl} 
                    alt={appointmentData.psychologist.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="font-medium text-lg text-gray-900">{appointmentData.psychologist.name}</h3>
                  <p className="text-sm text-gray-500">Psicólogo(a)</p>
                </div>
              </div>

              <Separator />

              {/* Appointment details */}
              <div className="grid grid-cols-1 gap-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Data:</span>
                  <span className="font-medium text-gray-900">
                    {format(appointmentData.date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Horário:</span>
                  <span className="font-medium text-gray-900">{appointmentData.time}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Duração:</span>
                  <span className="font-medium text-gray-900">{appointmentData.duration} minutos</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Modalidade:</span>
                  <span className="font-medium text-gray-900">Online</span>
                </div>
              </div>

              <Separator />

              {/* Pricing */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Valor da consulta:</span>
                  <span className="font-medium text-gray-900">R$ {consultationPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-lg pt-2 border-t border-gray-200">
                  <span className="font-semibold text-gray-900">Total:</span>
                  <span className="font-bold text-blue-600 text-xl">
                    R$ {totalPrice.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Payment Security Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <img 
                    src="https://http2.mlstatic.com/storage/logos-api-admin/51b446b0-571c-11e8-9a2d-4b2bd7b1bf77-m.svg" 
                    alt="Mercado Pago" 
                    className="h-6"
                  />
                  <span className="text-sm font-medium text-blue-900">Pagamento Seguro</span>
                </div>
                <p className="text-xs text-blue-800">
                  Seu pagamento será processado de forma segura pelo Mercado Pago. 
                  Aceitamos cartões de crédito, débito e PIX.
                </p>
              </div>

              {/* Error display */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {error}
                    {hasValidationError && (
                      <Button 
                        variant="link" 
                        className="p-0 ml-2 h-auto text-red-600 underline"
                        onClick={() => navigate('/schedule/step2')}
                      >
                        Escolher outro horário
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4 pt-6">
              {/* Payment button */}
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-base font-medium" 
                onClick={handlePayment}
                disabled={isLoading || isValidating || hasValidationError}
              >
                <CreditCard className="mr-2 h-5 w-5" />
                {isLoading ? "Processando..." : "Pagar com Mercado Pago"}
              </Button>
              
              {/* Security footer */}
              <p className="text-xs text-center text-gray-500">
                Pagamento seguro processado via Mercado Pago • SSL 256-bit
              </p>
            </CardFooter>
          </Card>
          
          {/* Back button */}
          <div className="flex justify-start">
            <Button variant="outline" onClick={handleBack} disabled={isLoading} className="gap-2">
              <ArrowLeft className="h-4 w-4" /> 
              Voltar
            </Button>
          </div>
        </div>
      </div>
    </RegistrationGuard>
  );
};

export default ScheduleStep3;
